/**
 * ModuleCompleteModal — celebratory popover that plays the premium 3D
 * "Module Complete" badge animation whenever a graduate finishes every
 * lesson in a module. Uses muted autoplay (browser policy safe) with a
 * one-tap Unmute control, and a Share/Continue CTA row.
 *
 * The Share button opens a mini share panel with LinkedIn, X (Twitter),
 * Facebook, WhatsApp, Copy link, and Download image options. Everything
 * points to a backend-generated OG landing page + 1200×630 PNG so previews
 * across every network show the actual "Module Complete" branded image.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  Sparkles, Volume2, VolumeX, ArrowRight, X, Share2, Linkedin, Twitter,
  Facebook, MessageCircle, Link as LinkIcon, Download,
} from 'lucide-react';

const MODULE_COMPLETE_VIDEO = '/videos/module-complete.mp4';

// Tiny WebAudio-generated celebratory chime — a bright two-note arpeggio.
// Uses a module-scoped AudioContext singleton so we don't hit browser
// per-page ctx creation limits when the modal opens repeatedly.
let _chimeCtx = null;
const playChime = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    if (!_chimeCtx || _chimeCtx.state === 'closed') _chimeCtx = new Ctx();
    const ctx = _chimeCtx;
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    const now = ctx.currentTime;
    const notes = [880, 1318.5]; // A5 → E6 (major fifth)
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, now + i * 0.14);
      gain.gain.setValueAtTime(0.0001, now + i * 0.14);
      gain.gain.exponentialRampToValueAtTime(0.18, now + i * 0.14 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.14 + 0.55);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.14);
      osc.stop(now + i * 0.14 + 0.6);
    });
  } catch (_) { /* silent */ }
};

const ModuleCompleteModal = ({
  open,
  moduleId,
  moduleTitle,
  courseTitle,
  holderName = '',
  streakDays = 0,
  apiBase = '',           // absolute origin of the backend, e.g. REACT_APP_BACKEND_URL
  onContinue,
  onClose,
  continueLabel = 'Continue to next module',
}) => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [ready, setReady] = useState(false);
  const [sharePanelOpen, setSharePanelOpen] = useState(false);

  // Compute the backend-hosted OG landing URL + PNG URL for this milestone.
  // Both use the public origin so social crawlers can fetch them.
  const shareLinks = useMemo(() => {
    if (!moduleId) return null;
    const origin = apiBase || (typeof window !== 'undefined' ? window.location.origin : '');
    const qs = new URLSearchParams();
    if (holderName) qs.set('holder', holderName);
    if (streakDays)  qs.set('streak', String(streakDays));
    const query = qs.toString();
    const landing = `${origin}/api/milestone/module/${moduleId}${query ? `?${query}` : ''}`;
    const image = `${origin}/api/milestone/module/${moduleId}/share-card.png${query ? `?${query}` : ''}`;
    return { landing, image };
  }, [moduleId, apiBase, holderName, streakDays]);

  const commentary = useMemo(() => {
    return [
      `Just finished the "${moduleTitle || 'next'}" module`,
      courseTitle ? `on ${courseTitle}` : '',
      '— one step closer to my Freeze Dry Academy certification.',
    ].filter(Boolean).join(' ');
  }, [moduleTitle, courseTitle]);

  useEffect(() => {
    if (!open) {
      setReady(false);
      setMuted(true);
      setSharePanelOpen(false);
      return undefined;
    }
    const el = videoRef.current;
    if (el) {
      el.muted = true;
      el.play().catch(() => { /* autoplay blocked — user will tap play */ });
    }
    playChime();
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const toggleMute = () => {
    const el = videoRef.current;
    if (!el) return;
    const next = !muted;
    el.muted = next;
    if (!next) el.play().catch(() => {});
    setMuted(next);
  };

  // ---------------------------------------------------------------------
  // Share handlers — each network gets its own deep link. The URL every
  // network unfurls is the backend OG landing page, which serves the PNG.
  // ---------------------------------------------------------------------
  const openWindow = (url) => window.open(url, '_blank', 'noopener,noreferrer,width=680,height=640');
  const shareLanding = shareLinks?.landing || '';
  const shareImage   = shareLinks?.image || '';

  const shareLinkedIn = () => openWindow(
    `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareLanding)}&summary=${encodeURIComponent(commentary)}`
  );
  const shareTwitter = () => openWindow(
    `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareLanding)}&text=${encodeURIComponent(commentary)}&hashtags=FreezeDryAcademy`
  );
  const shareFacebook = () => openWindow(
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLanding)}&quote=${encodeURIComponent(commentary)}`
  );
  const shareWhatsApp = () => openWindow(
    `https://api.whatsapp.com/send?text=${encodeURIComponent(`${commentary} ${shareLanding}`)}`
  );
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLanding);
      toast.success('Milestone link copied — paste it anywhere.');
    } catch {
      toast.error("Couldn't copy link — try again");
    }
  };
  const downloadImage = () => {
    if (!shareImage) return;
    // Download via anchor so the browser saves the file rather than opening it.
    const a = document.createElement('a');
    a.href = shareImage;
    a.download = `freeze-dry-academy-module-${moduleId || 'complete'}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  const nativeShare = async () => {
    if (!navigator.share) { setSharePanelOpen((v) => !v); return; }
    try {
      await navigator.share({
        title: `Module Complete: ${moduleTitle || 'Freeze Dry Academy'}`,
        text: commentary,
        url: shareLanding,
      });
    } catch { /* user cancelled — no-op */ }
  };

  const shareTargets = [
    { id: 'linkedin', label: 'LinkedIn', icon: Linkedin,       onClick: shareLinkedIn, style: 'bg-[#0A66C2] hover:bg-[#0A66C2]/90 text-white' },
    { id: 'twitter',  label: 'X',        icon: Twitter,        onClick: shareTwitter,  style: 'bg-black hover:bg-neutral-800 text-white' },
    { id: 'facebook', label: 'Facebook', icon: Facebook,       onClick: shareFacebook, style: 'bg-[#1877F2] hover:bg-[#1877F2]/90 text-white' },
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle,  onClick: shareWhatsApp, style: 'bg-[#25D366] hover:bg-[#25D366]/90 text-white' },
    { id: 'copy',     label: 'Copy link',icon: LinkIcon,       onClick: copyLink,      style: 'bg-background/15 hover:bg-background/30 text-background border border-background/30' },
    { id: 'download', label: 'Download', icon: Download,       onClick: downloadImage, style: 'bg-background/15 hover:bg-background/30 text-background border border-background/30' },
  ];

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-navy-950/90 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-labelledby="module-complete-title"
      data-testid="module-complete-modal"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg rounded-3xl overflow-hidden bg-navy-spotlight text-background shadow-2xl border border-amber-500/40 animate-in fade-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="module-complete-close"
          className="absolute top-3 right-3 z-10 h-8 w-8 rounded-full bg-background/10 hover:bg-background/25 text-background/80 hover:text-background flex items-center justify-center transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Ambient glow */}
        <div className="pointer-events-none absolute -top-24 -left-24 w-64 h-64 rounded-full bg-amber-500/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -right-24 w-64 h-64 rounded-full bg-primary/40 blur-3xl" />

        <div className="relative">
          {/* Video badge */}
          <div className="relative aspect-square w-full bg-navy-950">
            <video
              ref={videoRef}
              src={MODULE_COMPLETE_VIDEO}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              onLoadedData={() => setReady(true)}
              onCanPlay={() => setReady(true)}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="module-complete-video"
            />
            {!ready && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Sparkles className="w-10 h-10 text-amber-400 animate-pulse" />
              </div>
            )}
            <button
              type="button"
              onClick={toggleMute}
              aria-label={muted ? 'Unmute video' : 'Mute video'}
              data-testid="module-complete-mute-toggle"
              className="absolute bottom-3 right-3 h-9 w-9 rounded-full bg-background/15 hover:bg-background/30 text-background flex items-center justify-center backdrop-blur transition-colors"
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
          </div>

          {/* Body */}
          <div className="p-6 text-center">
            <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-amber-300">
              Module Complete
            </div>
            <h2
              id="module-complete-title"
              className="mt-2 font-heading text-2xl font-extrabold leading-tight"
              data-testid="module-complete-title"
            >
              {moduleTitle || 'Nicely done.'}
            </h2>
            {courseTitle && (
              <p className="mt-1 text-xs text-background/70">
                {courseTitle}
              </p>
            )}
            <p className="mt-3 text-sm text-background/85 max-w-sm mx-auto">
              You just cleared every lesson in this module. Your streak keeps building — one module closer to the certificate.
            </p>

            {/* Share panel — collapsible; targets each social network */}
            {sharePanelOpen && shareLinks && (
              <div
                className="mt-4 rounded-2xl border border-background/20 bg-background/5 p-4"
                data-testid="module-complete-share-panel"
              >
                <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-amber-300 mb-2">
                  Share the win
                </div>
                {/* Preview thumbnail — reassures the user what will unfurl on socials. */}
                <div className="rounded-lg overflow-hidden border border-background/15 mb-3">
                  <img
                    src={shareImage}
                    alt="Module complete milestone preview"
                    className="w-full h-auto block"
                    data-testid="module-complete-share-preview"
                    loading="lazy"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {shareTargets.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={t.onClick}
                      data-testid={`module-complete-share-${t.id}`}
                      className={`h-11 rounded-xl flex items-center justify-center gap-1.5 text-xs font-bold transition-colors ${t.style}`}
                    >
                      <t.icon className="w-4 h-4" />
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-5 flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-center">
              <Button
                onClick={nativeShare}
                variant="outline"
                className="rounded-full font-bold bg-background/10 border-background/40 text-background hover:bg-background/25 hover:text-background"
                data-testid="module-complete-share"
                aria-expanded={sharePanelOpen}
              >
                <Share2 className="w-4 h-4 mr-1" /> Share the win
              </Button>
              <Button
                onClick={onContinue || onClose}
                className="rounded-full font-bold bg-amber-500 hover:bg-amber-600 text-navy-950"
                data-testid="module-complete-continue"
              >
                {continueLabel} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModuleCompleteModal;
